import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/travelData';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.slug !== slug && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <main className="flex-1 pt-24 px-4">
        <div className="max-w-3xl mx-auto text-center py-20">
          <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const contentHtml = article.content
    .replace(/## (.+)/g, '<h2 class="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">$1</h2>')
    .replace(/### (.+)/g, '<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-2">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-foreground-900">$1</strong>')
    .replace(/- (.+)/g, '<li class="ml-4 mb-1 text-foreground-700 list-disc">$1</li>')
    .replace(/\n\n/g, '</p><p class="text-foreground-700 leading-relaxed mb-4">')
    .replace(/^/, '<p class="text-foreground-700 leading-relaxed mb-4">')
    .replace(/$/, '</p>');

  return (
    <main className="flex-1">
      <article>
        <section className="relative w-full h-64 md:h-96 overflow-hidden">
          <img
            src={article.coverUrl}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/50"></div>
        </section>

        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]} />

            <header className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="inline-block px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {article.categoryName}
                </span>
                <span className="text-sm text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <span>作者：{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
              </div>
            </header>

            <div
              className="prose-custom text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          </div>
        </section>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={ra.coverUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2">{ra.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "url": `https://rstuwlw.cn/article/${article.slug}`,
          "author": { "@type": "Person", "name": article.author },
          "datePublished": article.date,
          "description": article.excerpt,
          "articleSection": article.categoryName,
          "inLanguage": "zh-Hans",
          "publisher": {
            "@type": "Organization",
            "name": "亚洲天堂",
          },
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://rstuwlw.cn/" },
            { "@type": "ListItem", "position": 2, "name": article.categoryName, "item": `https://rstuwlw.cn/category/${article.category}` },
            { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://rstuwlw.cn/article/${article.slug}` },
          ],
        })
      }} />
    </main>
  );
}